import { Box, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';

import TabsPanel from '../../../components/TabsPanel';
import { settlementsAccountTabs } from '../../../constants/tabRoutes';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { getSettlement } from '../../../api/paymentsApi/settlementsApi';
import { SettlementContext, PaymentPreviewSummary } from './context/SettlementContext';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { formatDate } from '../../../helpers/formatDate';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import { useHasPermission } from '../../../hooks/useHasPermission';
import EditSettlementAmountsPopup from './EditSettlementAmountsPopup';
import moment from 'moment';

const SettlementAccountView = () => {
  const { settlementId } = useParams();
  const [entityRecordData, setEntityRecordData] = useState<{ [key: string]: unknown }>();
  const [, setPaymentPreview] = useState<PaymentPreviewSummary>();
  const [isEditAmountsPopupOpen, setIsEditAmountsPopupOpen] = useState(false);
  const canViewCases = useHasPermission('Cases', 'View');
  const paymentPreviewRefreshRef = useRef<() => void>(() => undefined);

  const registerPaymentPreviewRefresh = useCallback((refresh: () => void) => {
    paymentPreviewRefreshRef.current = refresh;
  }, []);

  const refreshPaymentPreview = useCallback(() => {
    paymentPreviewRefreshRef.current();
  }, []);

  const fetchSettlement = useCallback(() => {
    if (!settlementId) {
      return;
    }
    getSettlement(Number(settlementId)).then((res) => {
      if (res.status === 200) {
        const sequenceNumber = res.data.sequenceNumber;
        const hasDefendant = res.data.defendant !== null && Boolean(res.data.defendant.name.length);
        // const isToggleOn = res.data.isToggleOn;
        const mappedDeductions = res.data.deductions?.map((deduction) => ({
          ...deduction,
          amount: numberToUSDCurrency(deduction.amount),
          createdAt: formatDate(deduction.createdAt),
          isAssigned: deduction.isAssigned ? 'Yes' : 'No',
          // isAdded: res.data.withheld ? true : isToggleOn ? true : false,
          isAdded: deduction.included,
          deductionsId: deduction.id,
          preSettlementAmount: numberToUSDCurrency(deduction.preSettlementAmount),
        })).sort((a, b) => {
          const keyA = (a.vendorName || a.description || String(a.id)).toLowerCase();
          const keyB = (b.vendorName || b.description || String(b.id)).toLowerCase();
          return keyA.localeCompare(keyB);
        });

        const { leadFirm, secondaryFirms = [] } = res.data.attorneysFees;

        const mappedAttorneysFirms = [
          ...(leadFirm?.name
            ? [{
              ...leadFirm, fee: numberToUSDCurrency(leadFirm.fee),
              externalPaymentDate: leadFirm.externalPaymentDate ? moment(leadFirm.externalPaymentDate) : undefined,
            }]
            : []),

          ...secondaryFirms.map((f) => ({
            ...f,
            fee: numberToUSDCurrency(f.fee),
            externalPaymentDate: f.externalPaymentDate ? moment(f.externalPaymentDate) : undefined,
          })).sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          }),
        ];

        const mappedNetSettlement = {
          ...res.data.netSettlement,
          amount: numberToUSDCurrency(res.data.netSettlement.amount),
        }

        let netSettlementConfigs;
        if (
          res.data.config &&
          res.data.config.netSettlementConfigs &&
          Array.isArray(res.data.config.netSettlementConfigs) &&
          res.data.config.netSettlementConfigs.length > 0
        ) {
          const matchingConfig = res.data.config.netSettlementConfigs.find(
            (config) => config.sequenceNumber === sequenceNumber
          );

          if (matchingConfig) {
            netSettlementConfigs = {
              ...matchingConfig,
              externalPaymentDate: matchingConfig.externalPaymentDate ? moment(matchingConfig.externalPaymentDate) : '',
            };
          } else {
            netSettlementConfigs = {
              sequenceNumber: sequenceNumber,
              paymentIntention: res.data.netSettlement?.disposition || null,
              externalPaymentMethod: null,
              externalPaymentReference: null,
              externalPaymentMemo: null,
              externalPaymentDate: null,
            };
          }
        } else {
          netSettlementConfigs = {
            sequenceNumber: sequenceNumber,
            paymentIntention: res.data.netSettlement?.disposition || null,
            externalPaymentMethod: null,
            externalPaymentReference: null,
            externalPaymentMemo: null,
            externalPaymentDate: null,
          };
        }


        setEntityRecordData({
          id: res.data.id,
          caseReferenceId: res.data.case.referenceId,
          caseSystemName: res.data.case.name,
          settledAmount: res.data.settledAmount,
          pipAmount: res.data.pipAmount,
          lienReimbursementAmount: res.data.lienReimbursementAmount,
          settlementType: res.data.settlementType,
          isAccountingIntegrated: res.data.isAccountingIntegrated,
          plaintiff: {
            id: res.data.client.id,
            fullName: `${res.data.client.firstName} ${res.data.client.lastName}`,
          },
          defendant: hasDefendant
            ? {
              id: res.data.defendant.id,
              label: res.data.defendant.name,
            }
            : {
              id: 0,
              label: '',
            },
          deduction: mappedDeductions,
          status: res.data.status,
          caseId: res.data.case.id,
          secondaryFirms: res.data.secondaryFirms,
          firm: res.data.firm.name,
          attorneysFees: mappedAttorneysFirms,
          netSettlement: mappedNetSettlement,
          config: netSettlementConfigs,
          withheld: res.data.withheld,
          hasActiveOnboarding: res.data.hasActiveOnboarding,
        });
      }
    });
  }, [settlementId]);

  useEffect(() => {
    fetchSettlement();
  }, [fetchSettlement]);

  return (
    <SettlementContext.Provider value={{
      refreshSettlement: fetchSettlement,
      setPaymentPreviewSummary: setPaymentPreview,
      registerPaymentPreviewRefresh,
      refreshPaymentPreview,
    }}>
      <Box display="flex" justifyContent="flex-start" alignItems="center" gap="8px">
        <Typography sx={{
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Inter',
          color: '#ffffff',
          ml: '14px',
        }}>{`${entityRecordData?.caseSystemName}`}</Typography>
        {canViewCases && <FormLinkButton path={`/cases/${entityRecordData?.caseId}/home`} />}

      </Box>

      <Box display="flex" gap="12px" ml="14px" mt="8px">
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          backgroundColor: 'primary.light',
          borderRadius: '8px',
          padding: '6px 12px',
        }}>
          <Typography sx={{ fontSize: '12px', color: '#ffffff', fontFamily: 'Poppins' }}>Settlement Amount</Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#ffffff' }}>
            {numberToUSDCurrency(entityRecordData?.settledAmount as number)}
          </Typography>
          {entityRecordData?.status === 'Draft' && (
            <IconButton
              aria-label="edit amounts"
              onClick={() => setIsEditAmountsPopupOpen(true)}
              sx={{ color: '#ffffff', padding: '4px' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {entityRecordData?.settlementType === 'STANDARD_PIP' && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'primary.light',
            borderRadius: '8px',
            padding: '6px 12px',
          }}>
            <Typography sx={{ fontSize: '12px', color: '#ffffff', fontFamily: 'Poppins' }}>PIP Amount</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#ffffff' }}>
              {numberToUSDCurrency(entityRecordData?.pipAmount as number)}
            </Typography>
            {entityRecordData?.status === 'Draft' && (
              <IconButton
                aria-label="edit amounts"
                onClick={() => setIsEditAmountsPopupOpen(true)}
                sx={{ color: '#ffffff', padding: '4px' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
        {entityRecordData?.settlementType === 'LIEN_REPAYMENT' && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'primary.light',
            borderRadius: '8px',
            padding: '6px 12px',
          }}>
            <Typography sx={{ fontSize: '12px', color: '#ffffff', fontFamily:'Poppins'}}>Lien Reimbursement Amount</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#ffffff' }}>
              {numberToUSDCurrency(entityRecordData?.lienReimbursementAmount as number)}
            </Typography>
            {entityRecordData?.status === 'Draft' && (
              <IconButton
                aria-label="edit amounts"
                onClick={() => setIsEditAmountsPopupOpen(true)}
                sx={{ color: '#ffffff', padding: '4px' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Box display="flex" width="100%">
        <TabsPanel
          tabsList={settlementsAccountTabs}
          locationSlicePosition={4}
          contextData={entityRecordData}
          page={'Settlement'}
        />
      </Box>

      <EditSettlementAmountsPopup
        isOpen={isEditAmountsPopupOpen}
        onClose={() => setIsEditAmountsPopupOpen(false)}
        onSuccess={() => {
          fetchSettlement();
          refreshPaymentPreview();
        }}
        settlementId={Number(settlementId)}
        showPipAmount={entityRecordData?.settlementType === 'STANDARD_PIP'}
        initialSettlementAmount={entityRecordData?.settledAmount as number}
        initialPipAmount={entityRecordData?.pipAmount as number}
        showLienReimbursementAmount={entityRecordData?.settlementType === 'LIEN_REPAYMENT'}
        initialLienReimbursementAmount={entityRecordData?.lienReimbursementAmount as number}
      />
    </SettlementContext.Provider>
  );
};

export default SettlementAccountView;